import { computed, ref, set } from 'vue';
import { defineStore } from 'pinia';
import type {
  Migration as SchemaMigration,
  MigrationAdd,
  MigrationUpdate,
  MigrationDeploymentAdd,
  ScheduledTask
} from '@schema';
import api from '@/common/utils/api';
import useCommonStore from '@/common/store/pinia/common';
import mapper, { MappedMigration } from '@/common/store/pinia/migrations/migrationsMapper';
import { getDeploymentsUrl, getMigrationsUrl } from '@/common/store/pinia/maasUrls';
import { fileToBase64Content } from '@/common/utils/base64';

export interface CreateDeploymentOptions {
  vmName: string;
  cloneDisks?: boolean;
  force?: boolean;
  osMorphing?: boolean;
  saveSnapshot?: boolean;
  start?: boolean;
  windowsScriptFile?: File | null;
  linuxScriptFile?: File | null;
}

type MigrationStoreItem = SchemaMigration & {
  instances?: Record<string, object>;
};

export type MigrationAsideNavigationItem = {
  id: string;
  name: string;
};

async function toMigrationDeploymentAdd(options: CreateDeploymentOptions): Promise<MigrationDeploymentAdd> {
  const body: MigrationDeploymentAdd = {
    name: options.vmName,
    clone_disks: options.cloneDisks ?? false,
    force: options.force ?? false,
    os_morphing: options.osMorphing ?? false,
    save_snapshot: options.saveSnapshot ?? false,
    start: options.start ?? false
  };
  const filePromises: Promise<void>[] = [];
  if (options.windowsScriptFile) {
    const file = options.windowsScriptFile;
    filePromises.push(
      fileToBase64Content(file).then((encoded) => {
        body.windows_script_file = encoded;
      })
    );
  }
  if (options.linuxScriptFile) {
    const file = options.linuxScriptFile;
    filePromises.push(
      fileToBase64Content(file).then((encoded) => {
        body.linux_script_file = encoded;
      })
    );
  }
  return Promise.all(filePromises).then(() => body);
}

export const useMigrationsStore = defineStore('migrations', () => {
  const commonStore = useCommonStore<SchemaMigration, MappedMigration>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    plural: (data) => (data as { data: SchemaMigration[] }).data,
    mapItem: mapper,
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItems,
    remove,
    fetchItem,
    fetchItemsWithFiltersThrottled
  } = commonStore;

  const updateMigrationInstance = (id: string, vmId: string, instance: object) => {
    const currentItem = commonStore.itemsMap.value[id] as MigrationStoreItem | undefined;
    if (!currentItem) return;
    const updatedItem = {
      ...currentItem,
      instances: {
        ...(currentItem.instances ?? {}),
        [vmId]: {
          ...(currentItem.instances?.[vmId] ?? {}),
          ...instance
        }
      }
    };
    set(commonStore.itemsMap.value, id, updatedItem);
  };

  const migrations = computed(() => getItems.value);

  const asideNavigationItems = ref<MigrationAsideNavigationItem[]>([]);

  const fetchAsideNavigation = async () => {
    const response = await api.get<{ data: SchemaMigration[] }>(getMigrationsUrl());
    asideNavigationItems.value = response?.data?.data.map((row) => ({
      id: row.id ?? '',
      name: row.name ?? ''
    }));
  };

  const clearAsideNavigation = () => {
    asideNavigationItems.value = [];
  };

  const deleteMigration = (id: string) => remove(id);

  const deleteItems = (ids: string[]) => Promise.all(ids.map(id => deleteMigration(id)));

  const cleanupDisks = (id: string) => api.post(`${getMigrationsUrl()}/${id}/cleanup_disks`)
      .then(() => fetchItem(id));

  const createDeployment = (migrationId: string, options: CreateDeploymentOptions) => toMigrationDeploymentAdd(options)
      .then((body) => api.post(getDeploymentsUrl(migrationId), body))
      .then(() => fetchItem(migrationId));

  const addMigration = (body: MigrationAdd) => api.post<ScheduledTask>(getMigrationsUrl(), body);

  const updateMigration = (id: string, body: MigrationUpdate) => api.patch(`${getMigrationsUrl()}/${id}`, body)
    .then(() => fetchItem(id));

  const ws: SocketEvents = {
    maas(data: SocketEvent<{ migration_id?: string; replication_id?: string }>) {
      if (data.type !== 'migration' || !data.object?.migration_id) return;
      const id = data.object.migration_id;
      if (data.operation === 'created' || data.operation === 'updated' || data.operation === 'failed') {
        fetchItemsWithFiltersThrottled(id);
      } else if (data.operation === 'deleted') {
        fetchItemsWithFiltersThrottled.clearId(id);
        commonStore.removeItem(id);
      }
    }
  };

  return {
    ...commonStore,
    migrations,
    asideNavigationItems,
    fetchAsideNavigation,
    clearAsideNavigation,
    deleteMigration,
    deleteItems,
    cleanupDisks,
    createDeployment,
    updateMigrationInstance,
    addMigration,
    updateMigration,
    ws
  };
});

export default useMigrationsStore;
